/**
 * @documental
 * @export Heading
 * @import
 * import { Heading } from "capitalrx-components-2.0/lib/typography";
 * @use
 * The `Heading` component should be used as the primary heading for body content, beneath titles and subtitles and, alongside
 * the [Subheading](../Subheading/README.md) component.
 * @example
 * import { FC } from "react";
 * import { Heading, Paragraph } from "capitalrx-components-2.0/lib/typography";
 *
 * export const Component: FC = () => {
 *   return (
 *     <div>
 *       <Heading>Custom Heading</Heading>
 *       <Paragraph>
 *         The quick brown fox jumps over the lazy dog.
 *       </Paragraph>
 *     </div>
 *   );
 * };
 * @output
 * <div>
 *   <h4 class="heading"><span class="semibold">Custom Heading</span></h4>
 *   <p class="paragraph">
 *     The quick brown fox jumps over the lazy dog.
 *   </p>
 * </div>
 */
import { FC, ReactNode } from "react";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Semibold } from "../Semibold";
import { TextAlignment } from "../TextAlignment";

import tokenStyles from "./styles.module.css";

/**
 * The Heading component properties.
 */
export interface HeadingProps extends BaseProps {
  /**
   * The unique identifier for the heading element.
   */
  readonly id?: string;

  /**
   * The heading level. If not specified, defaults to `4`.
   * Values: `1`, `2`, `3`, `4`, `5`, `6`
   */
  readonly level?: 1 | 2 | 3 | 4 | 5 | 6;

  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;

  /** The component children. */
  readonly children?: ReactNode;
}

const useResolvedHeadingStyles = createComponentStyleHook<HeadingProps>({
  legacyStyles: () => import("./styles.module.scss"),
  tokenStyles,
  componentTokens: "heading",
});

/**
 * Displays body heading text. The heading is automatically spaced apart from its siblings. However, if the heading is the
 * first element it will not have any spacing above, and if it is the last element it will not have any spacing below.
 * Additionally, the heading is unaffected by text sizing applied to the [Body](../Body/README.md) component.
 */
export const Heading: FC<HeadingProps> = (props) => {
  const { children, textAlign = "left", id, level = 4 } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedHeadingStyles(props);
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.Heading, "heading", `align-${textAlign}`] });

  const HeadingTag = `h${level}` as const;

  return (
    <StyleModeBoundary mode={mode}>
      <HeadingTag {...baseHtmlProps} id={id}>
        <Semibold>{children}</Semibold>
      </HeadingTag>
    </StyleModeBoundary>
  );
};
